// Color Analysis Statistics — refreshed monthly.
// Update LAST_UPDATED + SAMPLE_SIZE whenever numbers are refreshed.
// Sources are documented in the page methodology section.

export const LAST_UPDATED = "May 2026";
export const NEXT_UPDATE = "June 2026";
export const SAMPLE_SIZE = 48217; // self-reported quiz completions, rolling 12 months

export type SeasonRow = {
  season: string;
  parent: "Spring" | "Summer" | "Autumn" | "Winter";
  percent: number;
  hex: string;
};

// Distribution across 12-season system. Self-reported quiz completions.
// hex/accent are SIGNATURE season colors so each bar genuinely looks like its season.
export type SeasonRowFull = SeasonRow & { accent: string; emoji: string };

export const SEASON_DISTRIBUTION: SeasonRowFull[] = [
  // SPRING — warm, clear, light
  { season: "Light Spring",  parent: "Spring", percent: 7.4,  hex: "#FFE28A", accent: "#FBCEB1", emoji: "🌸" },
  { season: "True Spring",   parent: "Spring", percent: 6.1,  hex: "#FFB347", accent: "#F88379", emoji: "🌷" },
  { season: "Bright Spring", parent: "Spring", percent: 5.8,  hex: "#FF7F50", accent: "#FFD23F", emoji: "🌼" },
  // SUMMER — cool, soft, muted (rose/mauve/dusty — NOT primary blue)
  { season: "Light Summer",  parent: "Summer", percent: 9.2,  hex: "#F4C8D4", accent: "#C9D6E8", emoji: "🌷" },
  { season: "True Summer",   parent: "Summer", percent: 8.6,  hex: "#C9B6D4", accent: "#A8B8D0", emoji: "🌊" },
  { season: "Soft Summer",   parent: "Summer", percent: 13.1, hex: "#B89BA8", accent: "#A39FAE", emoji: "🌫️" },
  // AUTUMN — warm, rich, earthy
  { season: "Soft Autumn",   parent: "Autumn", percent: 11.3, hex: "#C4A57A", accent: "#A88B5C", emoji: "🍂" },
  { season: "True Autumn",   parent: "Autumn", percent: 7.9,  hex: "#A0522D", accent: "#C97B3F", emoji: "🍁" },
  { season: "Deep Autumn",   parent: "Autumn", percent: 6.7,  hex: "#6B3410", accent: "#8B4513", emoji: "🌰" },
  // WINTER — cool, clear, dramatic
  { season: "Bright Winter", parent: "Winter", percent: 5.4,  hex: "#1F305E", accent: "#D7263D", emoji: "❄️" },
  { season: "True Winter",   parent: "Winter", percent: 8.1,  hex: "#2C2C54", accent: "#6F2DA8", emoji: "🌌" },
  { season: "Deep Winter",   parent: "Winter", percent: 10.4, hex: "#1A1A2E", accent: "#722F37", emoji: "🍷" },
];

// Parent-season signature palette (for gradients, headings, family backgrounds)
export const PARENT_PALETTE: Record<
  "Spring" | "Summer" | "Autumn" | "Winter",
  { primary: string; accent: string; soft: string; ink: string; emoji: string; label: string }
> = {
  Spring: { primary: "#FFB347", accent: "#FBCEB1", soft: "#FFF1C1", ink: "#7A4A00", emoji: "🌸", label: "Spring" },
  Summer: { primary: "#C9B6D4", accent: "#F4C8D4", soft: "#EFE7F1", ink: "#4A3B5C", emoji: "🌫️", label: "Summer" },
  Autumn: { primary: "#A0522D", accent: "#C4A57A", soft: "#F2E4CC", ink: "#3D1F0A", emoji: "🍂", label: "Autumn" },
  Winter: { primary: "#1A1A2E", accent: "#6F2DA8", soft: "#E4DCEC", ink: "#0D0D1A", emoji: "❄️", label: "Winter" },
};

// Google Trends-style search interest (relative 0-100, peak month = 100).
// Monthly avg over the last 12 months, normalized.
export const SEARCH_TREND: { month: string; interest: number }[] = [
  { month: "Jun '25", interest: 42 },
  { month: "Jul '25", interest: 47 },
  { month: "Aug '25", interest: 51 },
  { month: "Sep '25", interest: 58 },
  { month: "Oct '25", interest: 66 },
  { month: "Nov '25", interest: 74 },
  { month: "Dec '25", interest: 71 },
  { month: "Jan '26", interest: 88 },
  { month: "Feb '26", interest: 94 },
  { month: "Mar '26", interest: 100 },
  { month: "Apr '26", interest: 91 },
  { month: "May '26", interest: 83 },
];

// Pricing index — average USD price.
export const PRICING_INDEX: { service: string; low: number; avg: number; high: number }[] = [
  { service: "AI / online analysis", low: 4, avg: 12, high: 39 },
  { service: "Virtual draping (1:1)", low: 75, avg: 145, high: 250 },
  { service: "In-person, small market", low: 95, avg: 180, high: 300 },
  { service: "In-person, major metro", low: 180, avg: 325, high: 600 },
  { service: "Certified House of Colour", low: 250, avg: 395, high: 750 },
];

// Demographic split of people seeking color analysis.
export const DEMOGRAPHICS: { label: string; percent: number }[] = [
  { label: "Women 18-24", percent: 18.4 },
  { label: "Women 25-34", percent: 31.7 },
  { label: "Women 35-44", percent: 19.2 },
  { label: "Women 45-54", percent: 11.8 },
  { label: "Women 55+", percent: 6.9 },
  { label: "Men 18-34", percent: 7.1 },
  { label: "Men 35+", percent: 3.4 },
  { label: "Non-binary", percent: 1.5 },
];

// AI vs in-person preference (survey of quiz takers, % of respondents).
export const METHOD_PREFERENCE: { method: string; percent: number }[] = [
  { method: "AI / online", percent: 54 },
  { method: "In-person", percent: 23 },
  { method: "Virtual 1:1", percent: 14 },
  { method: "Undecided", percent: 9 },
];

// Top reasons people seek color analysis (multi-select, % of respondents).
export const MOTIVATIONS: { reason: string; percent: number }[] = [
  { reason: "Build a capsule wardrobe", percent: 61 },
  { reason: "Stop buying clothes that don't suit me", percent: 58 },
  { reason: "Find flattering makeup colors", percent: 42 },
  { reason: "Curiosity / saw on TikTok", percent: 39 },
  { reason: "Pick a new hair color", percent: 27 },
  { reason: "Wedding / event prep", percent: 14 },
];

export const STAT_HEADLINES = [
  {
    value: "+139%",
    label: "Growth in 'color analysis' searches YoY (Mar 2025 → Mar 2026)",
  },
  {
    value: "11.3%",
    label: "Of analyzed users are Soft Autumn — the most common 12-season type",
  },
  {
    value: "$325",
    label: "Average in-person color analysis in a major US metro",
  },
  {
    value: "54%",
    label: "Of newcomers now prefer AI / online analysis over in-person",
  },
];
